import { useEffect, useState } from 'react';
import { useAuth } from './use-auth';

interface MFASetupProps {
    onCancel: () => void;
}

export function MFASetup({ onCancel }: MFASetupProps) {
    const { setupMFA, verifyMFA, error } = useAuth();
    const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
    const [verificationCode, setVerificationCode] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [setupError, setSetupError] = useState<string | null>(null);

    useEffect(() => {
        const initSetup = async () => {
            try {
                const qrUrl = await setupMFA();
                setQrCodeUrl(qrUrl);
            } catch (err) {
                setSetupError(err instanceof Error ? err.message : 'Failed to set up MFA');
            }
        };

        void initSetup();
    }, [setupMFA]);

    const handleVerify = (e: React.FormEvent) => {
        e.preventDefault();
        setSetupError(null);
        setIsVerifying(true);

        void (async () => {
            try {
                await verifyMFA(verificationCode);
            } catch (err) {
                setSetupError(err instanceof Error ? err.message : 'Verification failed');
            } finally {
                setIsVerifying(false);
            }
        })();
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
            <div className="w-full max-w-md space-y-8 rounded-lg border bg-card p-8 shadow-lg">
                <div className="text-center">
                    <h2 className="text-3xl font-bold tracking-tight">Set Up MFA</h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Multi-factor authentication is required for all superuser accounts
                    </p>
                </div>

                {(error ?? setupError) && (
                    <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                        {error ?? setupError}
                    </div>
                )}

                {qrCodeUrl ? (
                    <div className="space-y-6">
                        <div className="text-center">
                            <p className="mb-4 text-sm text-muted-foreground">
                                Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                            </p>
                            <div className="mx-auto w-fit rounded-lg border bg-white p-4">
                                <img src={qrCodeUrl} alt="MFA QR Code" className="h-64 w-64" />
                            </div>
                        </div>

                        <form onSubmit={handleVerify} className="space-y-4">
                            <div>
                                <label htmlFor="code" className="block text-sm font-medium">
                                    Verification Code
                                </label>
                                <input
                                    id="code"
                                    name="code"
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    maxLength={6}
                                    required
                                    value={verificationCode}
                                    onChange={(e) => {
                                        setVerificationCode(e.target.value.replace(/\D/g, ''));
                                    }}
                                    className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-center text-2xl tracking-widest ring-offset-background placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                    placeholder="000000"
                                    autoFocus
                                />
                                <p className="mt-2 text-xs text-muted-foreground">
                                    Enter the 6-digit code from your authenticator app
                                </p>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={onCancel}
                                    className="flex-1 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isVerifying || verificationCode.length !== 6}
                                    className="flex-1 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {isVerifying ? 'Verifying...' : 'Verify & Continue'}
                                </button>
                            </div>
                        </form>
                    </div>
                ) : (
                    <div className="text-center">
                        <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-primary"></div>
                        <p className="text-sm text-muted-foreground">Generating QR code...</p>
                    </div>
                )}
            </div>
        </div>
    );
}
